import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/date';
import { formatMoeda } from '../../utils/fmt';
import * as api from '../../api/pedidos';
import * as modelosApi from '../../api/modelos';
import * as areasApi from '../../api/areas';
import * as tamanhosApi from '../../api/tamanhos';

export default function PedidoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pedido, setPedido] = useState(null);
  const [modelos, setModelos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tamanhos, setTamanhos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finalizando, setFinalizando] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  useEffect(() => {
    Promise.all([api.buscarId(id), modelosApi.listar(), tamanhosApi.listar()])
      .then(([p, m, tam]) => {
        setPedido(p.data); setModelos(m.data); setTamanhos(tam.data);
        return areasApi.listar(p.data.id_cliente_final).then(a => setAreas(a.data)).catch(() => setAreas([]));
      })
      .catch(() => navigate('/pedidos'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const modeloNome = mid => modelos.find(m => m.id_modelo_uniforme === mid)?.nome || mid;
  const areaNome = aid => aid ? (areas.find(a => a.id_area_cliente === aid)?.nome || '—') : null;
  const tamanhoNome = tid => tamanhos.find(t => t.id_tamanho_uniforme === tid)?.nome || tid;

  const handleFinalizar = async () => {
    if (!window.confirm(`Confirma a finalização do pedido ${pedido.numero_pedido}?\n\nIsso indica que o pedido foi entregue ao cliente.`)) return;
    setFinalizando(true);
    try {
      const { data } = await api.finalizar(pedido.id_pedido);
      setPedido(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao finalizar pedido.');
    } finally { setFinalizando(false); }
  };

  const handleCancelar = async () => {
    const aviso = `Cancelar o pedido ${pedido.numero_pedido}?\n\nComo operador/gestor/admin, isso força o cancelamento mesmo se a produção já tiver começado — confirme com quem estiver fabricando antes de continuar.`;
    if (!window.confirm(aviso)) return;
    setCancelando(true);
    try {
      const { data } = await api.cancelar(pedido.id_pedido);
      setPedido(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao cancelar pedido.');
    } finally { setCancelando(false); }
  };

  if (loading) return <LoadingSpinner />;
  if (!pedido) return null;

  const totalEntrega = pedido.itens?.reduce((s, i) => s + i.quantidade_pronta_entrega, 0) || 0;
  const totalFab = pedido.itens?.reduce((s, i) => s + i.quantidade_a_fabricar, 0) || 0;

  // Nesta tela só operador/gestor/admin entram (rota /pedidos/:id) — todos atuam como
  // "suporte", podendo forçar o cancelamento mesmo com produção já iniciada. O
  // cancelamento self-service do cliente (com a trava de produção) fica em MeusPedidos.
  const souStaff = ['operador', 'gestor', 'admin'].includes(user?.perfil);
  const podeFinalizador = souStaff;
  const podeCancelar = souStaff;

  return (
    <div>
      <PageHeader
        titulo={<>{pedido.numero_pedido} <StatusBadge status={pedido.status} /></>}
        subtitulo={
          pedido.status === 'finalizado'
            ? `Finalizado em ${formatDateTime(pedido.data_finalizacao)}`
            : pedido.status === 'cancelado'
              ? `Cancelado em ${formatDateTime(pedido.data_cancelamento)}`
              : `Pedido em ${formatDateTime(pedido.data_pedido)}`
        }
      >
        <button className="btn btn-outline-secondary btn-sm me-2" onClick={() => navigate(-1)}>← Voltar</button>
        {podeCancelar && pedido.status === 'aberto' && (
          <button
            className="btn btn-outline-danger btn-sm me-2"
            onClick={handleCancelar}
            disabled={cancelando}
          >
            {cancelando ? 'Cancelando…' : 'Cancelar Pedido'}
          </button>
        )}
        {podeFinalizador && pedido.status === 'aberto' && (
          <button
            className="btn btn-success btn-sm"
            onClick={handleFinalizar}
            disabled={finalizando}
          >
            {finalizando ? 'Finalizando…' : '✓ Finalizar Pedido'}
          </button>
        )}
      </PageHeader>

      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="cjc-stat-card">
            <div className="stat-label">Total</div>
            <div className="stat-value" style={{ fontSize: 22 }}>{formatMoeda(pedido.valor_total)}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="cjc-stat-card">
            <div className="stat-label">Total Peças</div>
            <div className="stat-value">{pedido.itens?.reduce((s, i) => s + i.quantidade, 0) || 0}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="cjc-stat-card">
            <div className="stat-label">Pronta Entrega</div>
            <div className="stat-value" style={{ color: 'var(--cjc-primary)' }}>{totalEntrega}</div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="cjc-stat-card">
            <div className="stat-label">A Fabricar</div>
            <div className="stat-value" style={{ color: totalFab > 0 ? 'var(--cjc-accent)' : 'var(--cjc-primary)' }}>
              {totalFab}
            </div>
          </div>
        </div>
      </div>

      {(pedido.endereco_entrega || areaNome(pedido.id_area_cliente)) && (
        <div className="card mb-3">
          <div className="card-body py-2 px-3" style={{ fontSize: 14 }}>
            {pedido.endereco_entrega && (
              <div><strong>Endereço de entrega:</strong> {pedido.endereco_entrega}</div>
            )}
            {areaNome(pedido.id_area_cliente) && (
              <div className={pedido.endereco_entrega ? 'mt-1' : ''}>
                <strong>Área:</strong> {areaNome(pedido.id_area_cliente)}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">Itens do Pedido</div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table mb-0">
              <thead>
                <tr>
                  <th>Modelo</th><th>Tam.</th><th>Qtd.</th>
                  <th>Preço Unit.</th><th>Subtotal</th>
                  <th>Pronta Entrega</th><th>A Fabricar</th>
                </tr>
              </thead>
              <tbody>
                {(pedido.itens || []).map(item => (
                  <tr key={item.id_pedido_item}>
                    <td className="fw-semibold">{modeloNome(item.id_modelo_uniforme)}</td>
                    <td><span className="badge bg-secondary">{tamanhoNome(item.id_tamanho_uniforme)}</span></td>
                    <td>{item.quantidade}</td>
                    <td>{formatMoeda(item.preco_unitario_snapshot)}</td>
                    <td className="fw-semibold">{formatMoeda(item.quantidade * item.preco_unitario_snapshot)}</td>
                    <td>
                      <span className={`fw-semibold ${item.quantidade_pronta_entrega > 0 ? 'text-success' : 'text-muted'}`}>
                        {item.quantidade_pronta_entrega}
                      </span>
                    </td>
                    <td>
                      {item.quantidade_a_fabricar > 0 ? (
                        <span className="fw-semibold text-danger">{item.quantidade_a_fabricar}</span>
                      ) : (
                        <span className="text-muted">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--cjc-gray-100)' }}>
                  <td colSpan={4} className="text-end fw-bold">Total do Pedido:</td>
                  <td className="fw-bold">{formatMoeda(pedido.valor_total)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
